// Pasture pins — the herdsman capture surface (plan pasture-pin-protocol.md
// Phase 2). Three screens in one page, phone-first like CensusCapture:
//
//   list    — today's assigned pins, grouped by paddock, plus the incursion
//             button (always one tap away).
//   walk    — live GPS distance to the pin (watchPosition); capture unlocks
//             within ~12 m. "Capture anyway" stays available — the server
//             FLAGS far captures, it never blocks a herdsman (fail-open).
//   capture — the guided wizard: 5 plate drops -> Photo A (meter in grass)
//             -> Photo B (straight down; level bubble when the phone has
//             orientation events) -> Photo C (at the server-chosen bearing)
//             -> taps -> save.
//
// Offline: photos land in the IndexedDB photo queue (photoQueue.js) keyed by
// client_obs_id; the JSON observation rides the existing offline queue via
// queuedMutation. The server links the two whichever side arrives first.
// Icon-heavy, minimal text (no i18n yet — plan's open question).

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle, Camera, Check, CheckCircle2, ChevronLeft, CloudOff,
  Crosshair, Footprints, Loader2, MapPin, Megaphone, Mountain, Ruler,
  Scissors, Sunrise, Sunset,
} from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import {
  createPinObservation, getPaddocks, getPinTasks, reportPinIncursion,
  uploadPinPhoto,
} from '../../services/api'
import { enqueuePhoto, flushPhotoQueue } from '../../lib/photoQueue'

const UNLOCK_RADIUS_M = 12

const KIND_META = {
  daily: { label: 'Daily pin', icon: Footprints, cls: 'bg-green-100 text-green-800' },
  away: { label: 'Resting paddock', icon: Mountain, cls: 'bg-blue-100 text-blue-800' },
  entry: { label: 'Entry reading', icon: Sunrise, cls: 'bg-amber-100 text-amber-800' },
  exit: { label: 'Exit reading', icon: Sunset, cls: 'bg-purple-100 text-purple-800' },
  fixed_point: { label: 'Photo point', icon: Camera, cls: 'bg-teal-100 text-teal-800' },
  calibration_cut: { label: 'Calibration cut', icon: Scissors, cls: 'bg-rose-100 text-rose-800' },
}

function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

function bearingDeg(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180
  const y = Math.sin(toRad(lng2 - lng1)) * Math.cos(toRad(lat2))
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
    - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng2 - lng1))
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
const cardinal = (deg) => CARDINALS[Math.round(deg / 45) % 8]

// ---------------------------------------------------------------- photo step

function PhotoStep({ slot, title, hint, file, onFile, levelGate, bearing }) {
  const inputRef = useRef(null)
  const [tilt, setTilt] = useState(null)   // null = no orientation events

  useEffect(() => {
    if (!levelGate) return undefined
    const onOrient = (e) => {
      if (e.beta == null || e.gamma == null) return
      setTilt(Math.max(Math.abs(e.beta), Math.abs(e.gamma)))
    }
    window.addEventListener('deviceorientation', onOrient)
    return () => window.removeEventListener('deviceorientation', onOrient)
  }, [levelGate])

  const level = tilt != null && tilt < 15
  const gateBlocks = levelGate && tilt != null && !level

  return (
    <div className="space-y-3">
      <div className="text-lg font-semibold text-gray-900">{title}</div>
      <p className="text-sm text-gray-500">{hint}</p>
      {bearing != null && (
        <div className="rounded-lg bg-teal-50 border border-teal-200 p-3 text-center">
          <div className="text-xs uppercase tracking-wide text-teal-700 font-semibold">Face this way</div>
          <div className="text-3xl font-bold text-teal-900">{bearing}° {cardinal(bearing)}</div>
        </div>
      )}
      {levelGate && (
        <div className={`rounded-lg border p-3 text-center text-sm font-medium ${tilt == null ? 'border-gray-200 text-gray-500' : level ? 'border-green-300 bg-green-50 text-green-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
          {tilt == null ? 'Hold the phone flat, facing down'
            : level ? 'Level — take the photo'
              : `Tilt ${Math.round(tilt)}° — hold it flat`}
        </div>
      )}
      <input
        ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
        capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f) }}
      />
      {file ? (
        <div className="space-y-2">
          <img src={URL.createObjectURL(file)} alt={`photo ${slot}`}
               className="w-full max-h-64 object-cover rounded-lg border border-gray-200" />
          <button onClick={() => inputRef.current?.click()}
                  className="w-full py-3 rounded-lg bg-gray-100 text-gray-700 font-medium active:bg-gray-200">
            Retake
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} disabled={gateBlocks}
                className="w-full py-6 rounded-lg bg-green-700 text-white text-lg font-semibold active:bg-green-800 disabled:opacity-40 flex items-center justify-center gap-2">
          <Camera className="h-6 w-6" /> Take photo
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- wizard

const EMPTY_READINGS = ['', '', '', '', '']

function CaptureWizard({ task, ranchId, fix, onDone, onBack }) {
  const [step, setStep] = useState(0)
  const [readings, setReadings] = useState(EMPTY_READINGS)
  const [swardCm, setSwardCm] = useState('')
  const [useSward, setUseSward] = useState(false)
  const [photos, setPhotos] = useState({ a: null, b: null, c: null })
  const [taps, setTaps] = useState({ dung: false, bare: false, unusual: false })
  const [note, setNote] = useState('')
  const [result, setResult] = useState(null)
  const [saving, setSaving] = useState(false)
  const queryClient = useQueryClient()

  const readingValues = readings.map((r) => parseFloat(r)).filter((v) => !Number.isNaN(v))
  const readingsOk = useSward ? swardCm !== '' : readingValues.length >= 1

  const save = async () => {
    setSaving(true)
    try {
      const clientObsId = crypto.randomUUID()
      for (const slot of ['a', 'b', 'c']) {
        if (photos[slot]) {
          await enqueuePhoto({
            clientObsId, slot, ranchId, blob: photos[slot],
            filename: photos[slot].name,
          })
        }
      }
      const res = await createPinObservation({
        ranch_id: ranchId,
        task_id: task.id,
        client_obs_id: clientObsId,
        observed_at: new Date().toISOString(),
        actual_lat: fix?.lat, actual_lng: fix?.lng, accuracy_m: fix?.accuracy,
        plate_readings: useSward ? null : readingValues,
        sward_cm: useSward && swardCm !== '' ? parseFloat(swardCm) : null,
        taps, note: note || null,
      })
      if (navigator.onLine) flushPhotoQueue(uploadPinPhoto)
      queryClient.invalidateQueries({ queryKey: ['pin-tasks'] })
      setResult({ queued: !!res?._offline_queued, flags: res?.flags || [] })
    } catch (err) {
      setResult({ error: err?.response?.data?.detail?.message
        || err?.response?.data?.detail || 'Could not save — try again.' })
    } finally {
      setSaving(false)
    }
  }

  if (result) {
    return (
      <div className="space-y-4 pb-28">
        {result.error ? (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex gap-3 items-start">
            <AlertTriangle className="h-6 w-6 text-red-600 shrink-0" />
            <div className="text-red-800">{String(result.error)}</div>
          </div>
        ) : result.queued ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex gap-3 items-start">
            <CloudOff className="h-6 w-6 text-amber-600 shrink-0" />
            <div className="text-amber-800">
              Saved on your phone — the reading and photos will sync
              automatically when you reconnect.
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex gap-3 items-start">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
            <div className="text-green-800">
              Pin recorded.
              {result.flags.length > 0 && (
                <span className="text-green-700"> ({result.flags.length} note{result.flags.length > 1 ? 's' : ''} for the office — nothing you need to fix)</span>
              )}
            </div>
          </div>
        )}
        <button onClick={onDone}
                className="w-full py-4 rounded-lg bg-green-700 text-white text-lg font-semibold active:bg-green-800">
          Back to today's pins
        </button>
      </div>
    )
  }

  const steps = [
    {
      key: 'readings',
      body: (
        <div className="space-y-3">
          <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Ruler className="h-5 w-5 text-green-700" /> Plate readings
          </div>
          <p className="text-sm text-gray-500">
            5 drops in a cross — one step each way from the pin. Step 1 m off
            dung or mounds; record what the meter reads.
          </p>
          {!useSward ? (
            <div className="grid grid-cols-5 gap-2">
              {readings.map((r, i) => (
                <input key={i} type="number" inputMode="decimal" value={r}
                       placeholder={`${i + 1}`}
                       onChange={(e) => setReadings(readings.map((x, j) => (j === i ? e.target.value : x)))}
                       className="px-1 py-4 text-xl text-center font-bold tabular-nums border border-gray-300 rounded-lg" />
              ))}
            </div>
          ) : (
            <input type="number" inputMode="decimal" value={swardCm}
                   placeholder="Grass height (cm)"
                   onChange={(e) => setSwardCm(e.target.value)}
                   className="w-full px-3 py-4 text-xl text-center font-bold tabular-nums border border-gray-300 rounded-lg" />
          )}
          <button onClick={() => setUseSward(!useSward)}
                  className="text-sm text-green-700 font-medium">
            {useSward ? 'I have a plate meter' : 'No meter? Use the sward stick'}
          </button>
        </div>
      ),
      ok: readingsOk,
    },
    {
      key: 'photo_a',
      body: <PhotoStep slot="a" title="Photo 1 — the meter in the grass"
                       hint="Meter standing in the grass, scale readable."
                       file={photos.a} onFile={(f) => setPhotos({ ...photos, a: f })} />,
      ok: true,
    },
    {
      key: 'photo_b',
      body: <PhotoStep slot="b" title="Photo 2 — straight down"
                       hint="From waist height, phone flat, looking straight down at the grass."
                       levelGate file={photos.b} onFile={(f) => setPhotos({ ...photos, b: f })} />,
      ok: true,
    },
    {
      key: 'photo_c',
      body: <PhotoStep slot="c" title="Photo 3 — the view"
                       hint="Landscape photo in the direction the app chose."
                       bearing={task.photo_bearing_deg}
                       file={photos.c} onFile={(f) => setPhotos({ ...photos, c: f })} />,
      ok: true,
    },
    {
      key: 'taps',
      body: (
        <div className="space-y-3">
          <div className="text-lg font-semibold text-gray-900">Quick checks</div>
          {[['dung', 'Dung or tracks here?'], ['bare', 'Bare soil patch?'],
            ['unusual', 'Anything unusual?']].map(([k, label]) => (
            <button key={k} onClick={() => setTaps({ ...taps, [k]: !taps[k] })}
                    className={`w-full py-4 rounded-lg text-lg font-semibold border-2 flex items-center justify-between px-4 ${taps[k] ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-gray-200 bg-white text-gray-600'}`}>
              {label} {taps[k] ? <Check className="h-6 w-6" /> : <span className="text-gray-300">—</span>}
            </button>
          ))}
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Anything to add? (optional)" rows={2}
                    className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg" />
        </div>
      ),
      ok: true,
    },
  ]

  const current = steps[step]
  const last = step === steps.length - 1
  const missingPhotos = ['a', 'b', 'c'].filter((s) => !photos[s]).length

  return (
    <div className="pb-28">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={step === 0 ? onBack : () => setStep(step - 1)}
                className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="h-6 w-6 text-gray-500" />
        </button>
        <div className="flex-1 flex gap-1">
          {steps.map((s, i) => (
            <div key={s.key} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-green-600' : 'bg-gray-200'}`} />
          ))}
        </div>
      </div>
      {current.body}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 z-20">
        <div className="max-w-xl mx-auto">
          {last ? (
            <button onClick={save} disabled={saving || !readingsOk}
                    className="w-full py-4 rounded-lg bg-green-700 text-white text-lg font-semibold active:bg-green-800 disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
              Save pin{missingPhotos > 0 ? ` (${3 - missingPhotos}/3 photos)` : ''}
            </button>
          ) : (
            <button onClick={() => setStep(step + 1)} disabled={!current.ok}
                    className="w-full py-4 rounded-lg bg-green-700 text-white text-lg font-semibold active:bg-green-800 disabled:opacity-50">
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- walk screen

function WalkToPin({ task, fix, onArrived, onBack }) {
  const distance = fix
    ? haversineM(fix.lat, fix.lng, task.lat, task.lng)
    : null
  const heading = fix
    ? bearingDeg(fix.lat, fix.lng, task.lat, task.lng)
    : null
  const unlocked = distance != null && distance <= UNLOCK_RADIUS_M
  const meta = KIND_META[task.kind] || KIND_META.daily

  return (
    <div className="pb-28">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="h-6 w-6 text-gray-500" />
        </button>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
          <meta.icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
        <span className="text-sm text-gray-500">{task.paddock_name}</span>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center space-y-2">
        <MapPin className="h-10 w-10 text-green-700 mx-auto" />
        {fix == null ? (
          <div className="text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Finding your position…
          </div>
        ) : (
          <>
            <div className="text-5xl font-bold tabular-nums text-gray-900">
              {distance < 1000 ? `${Math.round(distance)} m` : `${(distance / 1000).toFixed(1)} km`}
            </div>
            <div className="text-lg text-gray-600">
              Walk <b>{cardinal(heading)}</b> to the pin
            </div>
            <div className="text-xs text-gray-400">
              GPS ±{Math.round(fix.accuracy || 0)} m
            </div>
          </>
        )}
      </div>

      {task.kind === 'fixed_point' && task.monitoring_point_id && (
        <Link to={`/land/${task.monitoring_point_id}`}
              className="block mt-3 text-sm text-teal-700 font-medium">
          Open this photo point's record →
        </Link>
      )}

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 z-20">
        <div className="max-w-xl mx-auto space-y-2">
          <button onClick={onArrived} disabled={!unlocked}
                  className="w-full py-4 rounded-lg bg-green-700 text-white text-lg font-semibold active:bg-green-800 disabled:opacity-40 flex items-center justify-center gap-2">
            <Crosshair className="h-5 w-5" />
            {unlocked ? "I'm at the pin — start" : `Get within ${UNLOCK_RADIUS_M} m to start`}
          </button>
          {!unlocked && fix != null && (
            <button onClick={onArrived}
                    className="w-full py-2.5 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-sm font-medium">
              Capture anyway (will be marked far from the pin)
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- incursion

function IncursionSheet({ ranchId, onDone, onBack }) {
  const [paddockId, setPaddockId] = useState('')
  const [note, setNote] = useState('')
  const [photo, setPhoto] = useState(null)
  const [fix, setFix] = useState(null)
  const [result, setResult] = useState(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)

  const paddocksQ = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
    enabled: !!ranchId,
  })
  const paddocks = paddocksQ.data?.paddocks || paddocksQ.data || []

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setFix({ lat: pos.coords.latitude, lng: pos.coords.longitude,
                        accuracy: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 15000 },
    )
  }, [])

  const submit = async () => {
    setSaving(true)
    try {
      const clientObsId = crypto.randomUUID()
      if (photo) {
        await enqueuePhoto({ clientObsId, slot: 'incursion', ranchId,
                             blob: photo, filename: photo.name })
      }
      const res = await reportPinIncursion({
        ranch_id: ranchId, paddock_id: paddockId, client_obs_id: clientObsId,
        actual_lat: fix?.lat, actual_lng: fix?.lng, accuracy_m: fix?.accuracy,
        note: note || null,
      })
      if (navigator.onLine) flushPhotoQueue(uploadPinPhoto)
      setResult({ queued: !!res?._offline_queued })
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || 'Could not send — try again.' })
    } finally {
      setSaving(false)
    }
  }

  if (result) {
    return (
      <div className="space-y-4">
        {result.error ? (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-800">{String(result.error)}</div>
        ) : (
          <div className={`rounded-lg border p-4 flex gap-3 items-start ${result.queued ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
            {result.queued ? <CloudOff className="h-6 w-6 text-amber-600 shrink-0" /> : <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />}
            <div className={result.queued ? 'text-amber-800' : 'text-green-800'}>
              {result.queued
                ? 'Report saved on your phone — it will reach the office when you reconnect.'
                : 'Incursion reported — the office has been alerted. Reporting never counts against you.'}
            </div>
          </div>
        )}
        <button onClick={onDone} className="w-full py-4 rounded-lg bg-green-700 text-white text-lg font-semibold">Done</button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-28">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="h-6 w-6 text-gray-500" />
        </button>
        <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-red-600" /> Report outside livestock
        </div>
      </div>
      <select value={paddockId} onChange={(e) => setPaddockId(e.target.value)}
              className="w-full px-3 py-4 text-lg border border-gray-300 rounded-lg bg-white">
        <option value="">Which paddock?</option>
        {paddocks.map((p) => (
          <option key={p.id} value={p.id}>{p.name || p.code}</option>
        ))}
      </select>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
             capture="environment" className="hidden"
             onChange={(e) => { const f = e.target.files?.[0]; if (f) setPhoto(f) }} />
      <button onClick={() => inputRef.current?.click()}
              className="w-full py-4 rounded-lg bg-gray-100 text-gray-700 font-medium flex items-center justify-center gap-2 active:bg-gray-200">
        <Camera className="h-5 w-5" /> {photo ? 'Photo attached — retake' : 'Add a photo (optional)'}
      </button>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="What did you see?"
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg" />
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 z-20">
        <div className="max-w-xl mx-auto">
          <button onClick={submit} disabled={saving || !paddockId}
                  className="w-full py-4 rounded-lg bg-red-600 text-white text-lg font-semibold active:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Megaphone className="h-5 w-5" />}
            Send report
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- page

export default function PasturePage({ ranchId }) {
  const [view, setView] = useState('list')     // list | walk | capture | incursion
  const [task, setTask] = useState(null)
  const [fix, setFix] = useState(null)
  const watchRef = useRef(null)

  const tasksQ = useQuery({
    queryKey: ['pin-tasks', ranchId],
    queryFn: () => getPinTasks(ranchId, { status: 'all' }),
    enabled: !!ranchId,
  })

  // One watchPosition for walk + capture; GPS stays warm through the wizard.
  useEffect(() => {
    if (view !== 'walk' && view !== 'capture') {
      if (watchRef.current != null) {
        navigator.geolocation?.clearWatch(watchRef.current)
        watchRef.current = null
      }
      return undefined
    }
    if (watchRef.current == null && navigator.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => setFix({ lat: pos.coords.latitude, lng: pos.coords.longitude,
                          accuracy: pos.coords.accuracy }),
        () => {},
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 2000 },
      )
    }
    return undefined
  }, [view])

  const tasks = tasksQ.data?.tasks || []
  const byPaddock = useMemo(() => {
    const groups = new Map()
    for (const t of tasks) {
      if (!groups.has(t.paddock_id)) groups.set(t.paddock_id, { name: t.paddock_name, code: t.paddock_code, tasks: [] })
      groups.get(t.paddock_id).tasks.push(t)
    }
    return [...groups.values()]
  }, [tasks])
  const pendingCount = tasks.filter((t) => t.status === 'pending').length

  return (
    <div className="max-w-xl mx-auto">
      {view === 'list' && (
        <>
          <PageHeader icon={Footprints} title="Pasture"
                      subtitle="Walk to each pin, read the grass, take the photos. The app guides every step." />
          <button onClick={() => setView('incursion')}
                  className="w-full mb-4 py-3.5 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 font-semibold flex items-center justify-center gap-2 active:bg-red-100">
            <Megaphone className="h-5 w-5" /> Report outside livestock
          </button>

          {tasksQ.isLoading && <p className="text-gray-500 text-sm">Loading today's pins…</p>}
          {!tasksQ.isLoading && tasks.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-500">
              No pins assigned today.
            </div>
          )}
          {byPaddock.map((group) => (
            <div key={group.code} className="mb-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">
                {group.name} <span className="text-gray-300">· {group.code}</span>
              </div>
              <div className="space-y-2">
                {group.tasks.map((t) => {
                  const meta = KIND_META[t.kind] || KIND_META.daily
                  const done = t.status === 'done'
                  return (
                    <button key={t.id} disabled={done}
                            onClick={() => { setTask(t); setFix(null); setView('walk') }}
                            className={`w-full rounded-lg border p-4 flex items-center gap-3 text-left ${done ? 'border-green-200 bg-green-50 opacity-70' : 'border-gray-200 bg-white active:bg-gray-50'}`}>
                      <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full ${meta.cls}`}>
                        {done ? <Check className="h-5 w-5" /> : <meta.icon className="h-5 w-5" />}
                      </span>
                      <span className="flex-1">
                        <span className="block font-semibold text-gray-900">{meta.label} #{t.seq}</span>
                        <span className="block text-xs text-gray-500">
                          {done ? 'Done' : t.status === 'missed' ? 'Missed — can still be done' : 'Tap to walk there'}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {pendingCount > 0 && (
            <p className="text-center text-xs text-gray-400 mt-2">{pendingCount} pin{pendingCount > 1 ? 's' : ''} to go today</p>
          )}
        </>
      )}

      {view === 'walk' && task && (
        <WalkToPin task={task} fix={fix}
                   onArrived={() => setView('capture')}
                   onBack={() => setView('list')} />
      )}
      {view === 'capture' && task && (
        <CaptureWizard task={task} ranchId={ranchId} fix={fix}
                       onDone={() => { setTask(null); setView('list') }}
                       onBack={() => setView('walk')} />
      )}
      {view === 'incursion' && (
        <IncursionSheet ranchId={ranchId}
                        onDone={() => setView('list')}
                        onBack={() => setView('list')} />
      )}
    </div>
  )
}
